import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sparkle, ArrowsClockwise, DownloadSimple, Copy, PaperPlaneTilt, PencilSimple, Eye } from "@phosphor-icons/react";
import { Frame } from "lucide-react";
import { toast } from "sonner";
import { useApi } from "@/api/useApi";
import { useRole } from "@/hooks/useRole";
import ImageEditor from "@/components/ImageEditor";
import BrandFrame from "@/components/BrandFrame";
import { FullPreviewModal } from "@/components/FullPreview/FullPreviewModal";

const STYLES = [
  { key: "photorealistic", label: "Photorealistic" },
  { key: "lifestyle", label: "Lifestyle" },
  { key: "product", label: "Product" },
  { key: "minimal", label: "Minimal" },
  { key: "bold_graphic", label: "Bold Graphic" },
];
const RATIOS = [
  { key: "1:1", label: "Square 1:1" },
  { key: "4:5", label: "Portrait 4:5" },
  { key: "16:9", label: "Landscape 16:9" },
  { key: "9:16", label: "Story 9:16" },
];
const aspectClass = { "1:1": "aspect-square", "4:5": "aspect-[4/5]", "16:9": "aspect-video", "9:16": "aspect-[9/16]" };

export default function Images() {
  const api = useApi();
  const { canManageContent } = useRole();
  const [form, setForm] = useState({ prompt: "", style: "photorealistic", aspect_ratio: "1:1", negative_prompt: "" });
  const [loading, setLoading] = useState(false);
  const [images, setImages] = useState([]);
  const [busyIdx, setBusyIdx] = useState(null);
  const [refineOpen, setRefineOpen] = useState(false);
  const [refineIdx, setRefineIdx] = useState(null);
  const [refineText, setRefineText] = useState("");
  const [sentIdx, setSentIdx] = useState({});
  const [editImg, setEditImg] = useState(null);
  const [frameImg, setFrameImg] = useState(null);
  const [fullPreview, setFullPreview] = useState(null);

  if (!canManageContent) {
    return <div className="text-[#7A9090] p-8" data-testid="images-no-access">You don't have access to AI Image Generation.</div>;
  }

  const generate = async (overrides = {}) => {
    const payload = { ...form, count: 4, ...overrides };
    if (!payload.prompt.trim()) { toast.error("Describe the image first"); return; }
    setLoading(true);
    try {
      const res = await api.post("/images/generate", payload);
      setImages(res.data.images || []);
      setSentIdx({});
      if (!(res.data.images || []).length) toast.error("No images returned — try again");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Image generation failed");
    } finally { setLoading(false); }
  };

  const refineOne = async () => {
    if (!refineText.trim()) { toast.error("Describe what to change"); return; }
    setRefineOpen(false);
    setBusyIdx(refineIdx);
    try {
      const res = await api.post("/images/generate", {
        prompt: `${form.prompt}. ${refineText}`, style: form.style, aspect_ratio: form.aspect_ratio, negative_prompt: form.negative_prompt, count: 1,
      });
      const newImg = res.data.images?.[0];
      if (newImg) setImages((prev) => prev.map((im, i) => (i === refineIdx ? newImg : im)));
      toast.success("Variation refined");
    } catch (e) { toast.error(e?.response?.data?.detail || "Refine failed"); }
    finally { setBusyIdx(null); setRefineText(""); }
  };

  const sendToReview = async (img, idx) => {
    setBusyIdx(idx);
    try {
      await api.post("/images/send-to-review", { data_url: img.data_url, prompt: form.prompt, platform: "instagram" });
      setSentIdx((s) => ({ ...s, [idx]: true }));
      toast.success("Sent to Approval Queue (Needs review)");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to send"); }
    finally { setBusyIdx(null); }
  };

  const download = (img, idx) => {
    const a = document.createElement("a");
    a.href = img.data_url;
    a.download = `ai-image-${idx + 1}.png`;
    a.click();
  };

  const copy = async (img) => {
    try {
      const blob = await (await fetch(img.data_url)).blob();
      await navigator.clipboard.write([new window.ClipboardItem({ [blob.type]: blob })]);
      toast.success("Image copied");
    } catch { toast.error("Copy not supported in this browser — use Download"); }
  };

  return (
    <div className="space-y-8" data-testid="images-container">
      <div>
        <h1 className="text-4xl font-bold text-[#3C4048] mb-2 tracking-tight" style={{ fontFamily: "Montserrat", fontWeight: 800 }}>AI Image Generation</h1>
        <p className="text-[#7A9090]">Generate 4 on-brand image variations, then send the best to the approval queue.</p>
      </div>

      <div className="bg-white border border-[#e5e7eb] rounded-[12px] p-6 space-y-4">
        <div>
          <Label className="text-[#7A9090] text-xs font-semibold uppercase tracking-wider mb-1.5 block">Prompt</Label>
          <Textarea data-testid="image-prompt" value={form.prompt} onChange={(e) => setForm((f) => ({ ...f, prompt: e.target.value }))} placeholder="e.g. a therapist's hands performing deep tissue massage on a client's shoulder, calm clinic room" className="border-[#e5e7eb] text-[#3C4048] min-h-[80px]" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-[#7A9090] text-xs font-semibold uppercase tracking-wider mb-1.5 block">Style</Label>
            <select data-testid="image-style" value={form.style} onChange={(e) => setForm((f) => ({ ...f, style: e.target.value }))} className="w-full border border-[#e5e7eb] rounded-[10px] p-2 text-sm text-[#3C4048]">
              {STYLES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-[#7A9090] text-xs font-semibold uppercase tracking-wider mb-1.5 block">Aspect ratio</Label>
            <select data-testid="image-ratio" value={form.aspect_ratio} onChange={(e) => setForm((f) => ({ ...f, aspect_ratio: e.target.value }))} className="w-full border border-[#e5e7eb] rounded-[10px] p-2 text-sm text-[#3C4048]">
              {RATIOS.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
            </select>
          </div>
        </div>
        <div>
          <Label className="text-[#7A9090] text-xs font-semibold uppercase tracking-wider mb-1.5 block">Exclude (optional)</Label>
          <Input data-testid="image-negative" value={form.negative_prompt} onChange={(e) => setForm((f) => ({ ...f, negative_prompt: e.target.value }))} placeholder="e.g. text, logos, dark background" className="border-[#e5e7eb] text-[#3C4048]" />
        </div>
        <Button data-testid="generate-images" onClick={() => generate()} disabled={loading} className="bg-[#5FA8B4] hover:bg-[#3A7A8C] text-white gap-2 rounded-[12px]">
          <Sparkle size={18} weight="fill" /> {loading ? "Generating 4 images…" : "Generate 4 variations"}
        </Button>
      </div>

      {loading && <p className="text-[#7A9090]" data-testid="images-loading">This can take up to a minute…</p>}

      {images.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          {images.map((img, idx) => (
            <div key={idx} data-testid={`image-card-${idx}`} className="bg-white border border-[#e5e7eb] rounded-[12px] overflow-hidden">
              <div className={`bg-[#f5f5f5] ${aspectClass[img.aspect_ratio] || "aspect-square"} overflow-hidden flex items-center justify-center`}>
                <img src={img.data_url} alt={`variation ${idx + 1}`} className="w-full h-full object-cover" />
              </div>
              <div className="p-3 flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={() => setFullPreview({ type: "image", data: { mediaUrl: img.data_url, filename: `ai-image-${idx + 1}.png` } })}
                  className="border-[#5FA8B4] text-[#3A7A8C] hover:bg-[#f0f7f8] gap-1 rounded-[10px] h-8" data-testid={`full-preview-image-${idx}`}>
                  <Eye size={14} /> Full Preview
                </Button>
                <Button size="sm" disabled={busyIdx === idx || sentIdx[idx]} onClick={() => sendToReview(img, idx)} className="bg-[#5FA8B4] hover:bg-[#3A7A8C] text-white gap-1 rounded-[10px] h-8" data-testid={`send-image-${idx}`}>
                  <PaperPlaneTilt size={14} /> {sentIdx[idx] ? "Sent ✓" : "Send to Approval"}
                </Button>
                <Button size="sm" variant="outline" disabled={busyIdx === idx} onClick={() => { setRefineIdx(idx); setRefineText(""); setRefineOpen(true); }} className="border-[#e5e7eb] text-[#3C4048] hover:bg-[#f5f5f5] gap-1 rounded-[10px] h-8" data-testid={`refine-image-${idx}`}>
                  <ArrowsClockwise size={14} /> Refine
                </Button>
                <Button size="sm" variant="outline" onClick={() => setEditImg(img)} className="border-[#e5e7eb] text-[#3C4048] hover:bg-[#f5f5f5] gap-1 rounded-[10px] h-8" data-testid={`edit-image-${idx}`}>
                  <PencilSimple size={14} /> Edit
                </Button>
                <Button size="sm" variant="outline" onClick={() => setFrameImg(img)} className="border-[#5FA8B4] text-[#3A7A8C] hover:bg-[#f0f7f8] gap-1 rounded-[10px] h-8" data-testid={`frame-image-${idx}`}>
                  <Frame size={14} /> Brand Frame
                </Button>
                <Button size="sm" variant="ghost" onClick={() => download(img, idx)} className="text-[#3A7A8C] hover:bg-[#f0f7f8] gap-1 rounded-[10px] h-8" data-testid={`download-image-${idx}`}>
                  <DownloadSimple size={14} /> Download
                </Button>
                <Button size="sm" variant="ghost" onClick={() => copy(img)} className="text-[#3A7A8C] hover:bg-[#f0f7f8] gap-1 rounded-[10px] h-8" data-testid={`copy-image-${idx}`}>
                  <Copy size={14} /> Copy
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={refineOpen} onOpenChange={setRefineOpen}>
        <DialogContent className="bg-white border-[#e5e7eb] max-w-lg">
          <DialogHeader><DialogTitle className="text-[#3C4048]" style={{ fontFamily: "Montserrat", fontWeight: 800 }}>Refine this image</DialogTitle></DialogHeader>
          <p className="text-sm text-[#7A9090] -mt-2">Describe what to change (e.g. "brighter", "add a plant in the background", "closer crop").</p>
          <Textarea data-testid="image-refine-instruction" value={refineText} onChange={(e) => setRefineText(e.target.value)} className="border-[#e5e7eb] text-[#3C4048] min-h-[90px]" />
          <div className="flex gap-3">
            <Button data-testid="image-refine-submit" onClick={refineOne} className="flex-1 bg-[#5FA8B4] hover:bg-[#3A7A8C] text-white rounded-[12px]">Regenerate variation</Button>
            <Button onClick={() => setRefineOpen(false)} variant="outline" className="border-[#e5e7eb] text-[#3C4048] rounded-[12px]">Cancel</Button>
          </div>
        </DialogContent>
      </Dialog>

      {editImg && (
        <ImageEditor
          open={!!editImg}
          onOpenChange={(v) => !v && setEditImg(null)}
          src={editImg.data_url}
          filename={`ai-image-${Date.now()}.png`}
          onSaved={() => { setEditImg(null); toast.success("Edited image saved to Media Library"); }}
        />
      )}

      {frameImg && (
        <BrandFrame
          open={!!frameImg}
          onOpenChange={(v) => !v && setFrameImg(null)}
          src={frameImg.data_url}
          filename={`ai-image-${Date.now()}.png`}
          onSaved={() => { setFrameImg(null); toast.success("Branded image saved to Media Library"); }}
        />
      )}

      <FullPreviewModal open={!!fullPreview} onOpenChange={(v) => !v && setFullPreview(null)} preview={fullPreview} />
    </div>
  );
}
